import { useState } from "react";
import { toast } from "sonner";
import { Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

export const MapGenerateDialog = ({ open, onOpenChange, uid, locationCount, onSaved }) => {
  const [form, setForm] = useState({ name: "", description: "", use_ai: locationCount > 0, seed: "" });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/universes/${uid}/maps/generate`, {
        name: form.name || "Generated world", description: form.description, use_ai: form.use_ai,
        seed: form.seed === "" ? null : parseInt(form.seed, 10),
      });
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent data-testid="map-generate-dialog">
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">Generate a world map</DialogTitle>
            <DialogDescription>The AI cartographer reads your Location entries and lays out land, water and biomes. You can paint over everything afterwards.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-6">
            <div className="space-y-2"><Label>Map name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="The Known World" data-testid="gen-map-name-input" /></div>
            <div className="space-y-2">
              <Label>Describe the geography (optional)</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="A crescent continent wrapped around an inland sea, ice in the north, the Ashen Coast a volcanic desert in the east…" data-testid="gen-map-description-input" />
            </div>
            <label className="flex items-center gap-2 text-sm text-[#E8DFD1]">
              <Checkbox checked={form.use_ai} disabled={locationCount === 0 && !form.description.trim()} onCheckedChange={(v) => setForm({ ...form, use_ai: !!v })} data-testid="gen-map-use-ai" />
              Use AI with my {locationCount} Location {locationCount === 1 ? "entry" : "entries"} (places pins automatically)
            </label>
            <div className="space-y-2 w-40"><Label>Seed</Label><Input type="number" className="font-mono-lf" value={form.seed} onChange={(e) => setForm({ ...form, seed: e.target.value })} placeholder="random" data-testid="gen-map-seed-input" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="gen-map-submit-button"><Wand2 className="h-4 w-4 mr-1.5" />{busy ? (form.use_ai ? "Charting the world…" : "Generating…") : "Generate"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
